/**
 * Калькулятор расчёта товаров заявки менеджера.
 *
 * Backend-driven: все расчёты выполняются на сервере (POST /manager/calculator/calculate),
 * фронт собирает input-данные, отправляет money как integer cents и отображает
 * результат, переводя cents → EUR-decimal через format helpers.
 *
 * H.2.b/c: контракт фронта/бэка для money — int cents.
 *
 * Публичные функции:
 *   calculateProduct(productId)   — запуск с debounce 300мс
 *   calculateAllProducts()        — пересчёт всех товаров на странице
 *   updateGrandTotals()           — итоги по всем брендам
 *   collectProductData(productId) — собрать данные для сохранения/approve
 */

import { apiRequest, APIError } from '../../api.js';
import { state } from './store.js';

const CALCULATE_DEBOUNCE_MS = 300;

const eurToCents = (eur) => {
    const n = parseFloat(eur);
    if (!isFinite(n) || isNaN(n)) return 0;
    return Math.round(n * 100);
};

const centsToEur = (cents) => {
    const n = typeof cents === 'number' ? cents : parseInt(cents, 10);
    return isFinite(n) ? n / 100 : 0;
};

// Форматирование cents → "1234.56" для UI-display.
function fmtCents(cents) {
    return centsToEur(cents).toFixed(2);
}

// DOM-helpers с фабрикой по productId — инкапсулируют именования полей калькулятора
function inputValueFloat(name, productId) {
    const el = document.querySelector(`input[name="${name}_${productId}"]`);
    return parseFloat(el?.value) || 0;
}

function inputValueRaw(name, productId) {
    const el = document.querySelector(`input[name="${name}_${productId}"]`);
    return el?.value || 0;
}

function textareaValue(name, productId) {
    const el = document.querySelector(`textarea[name="${name}_${productId}"]`);
    return el?.value || '';
}

function setText(id, value) {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
}

function textById(id) {
    const el = document.getElementById(id);
    return parseFloat(el?.textContent) || 0;
}

async function performCalculation(productId) {
    // H.2.c: money-поля → cents (× 100), percent — float как есть.
    const data = {
        rrp: eurToCents(document.getElementById('rrp_' + productId)?.value || 0),
        discount_rrp_percent: inputValueFloat('discount_rrp', productId),
        extra_discount_percent: inputValueFloat('discount_extra', productId),
        dealer_tax_percent: inputValueFloat('dealer_tax', productId),
        invoice_other: eurToCents(inputValueFloat('invoice_other_discount', productId)),
        service_fee_percent: inputValueFloat('complete_tax', productId),
        complete_other1_percent: inputValueFloat('complete_other', productId),
        complete_other2: eurToCents(inputValueFloat('complete_other_2', productId)),
        other_other1: eurToCents(inputValueFloat('other_1', productId)),
        other_other2: eurToCents(inputValueFloat('other_2', productId)),
        trade_discount_percent: inputValueFloat('trade_discount', productId),
        quantity: parseInt(inputValueRaw('qty', productId), 10) || 1,
    };

    try {
        const response = await apiRequest('/manager/calculator/calculate', {
            method: 'POST',
            body: data,
        });

        if (response && response.success && response.data) {
            updateProductUI(productId, response.data);
        } else {
            console.error('Calculation response missing data:', response);
        }
    } catch (e) {
        console.error('Calculation error for product ' + productId + ':', e);
        if (e instanceof APIError && e.details && e.details.fields) {
            console.error('Validation errors:', e.details.fields);
        }
        if (typeof window.showToast === 'function') {
            window.showToast('error', 'Calculation error. Please check input values.');
        }
    }
}

export function calculateProduct(productId) {
    if (state.calculateDebounceTimers[productId]) {
        clearTimeout(state.calculateDebounceTimers[productId]);
    }
    state.calculateDebounceTimers[productId] = setTimeout(() => {
        performCalculation(productId);
    }, CALCULATE_DEBOUNCE_MS);
}

function updateProductUI(productId, calc) {
    // Все money-поля в response — int cents.
    if (calc.invoicePrice) {
        setText('discount_rrp_val_' + productId, fmtCents(calc.invoicePrice.discountRrpAmount));
        setText('discount_extra_val_' + productId, fmtCents(calc.invoicePrice.extraDiscountAmount));
        setText('dealer_tax_val_' + productId, fmtCents(calc.invoicePrice.dealerTaxAmount));
        setText('invoice_other_discount_val_' + productId, fmtCents(calc.invoicePrice.otherAmount));
        setText('invoice_total_' + productId, fmtCents(calc.invoicePrice.invoiceTotal));
        setText('final_invoice_' + productId, fmtCents(calc.invoicePrice.invoiceTotal));
    }

    if (calc.complete) {
        setText('service_fee_val_' + productId, fmtCents(calc.complete.serviceFeeAmount));
        setText('complete_other_val_' + productId, fmtCents(calc.complete.other1Amount));
        setText('complete_other_2_val_' + productId, parseFloat(inputValueFloat('complete_other_2', productId)).toFixed(2));
        setText('complete_total_' + productId, fmtCents(calc.complete.completeTotal));
        setText('final_complete_' + productId, fmtCents(calc.complete.completeTotal));
    }

    if (calc.other) {
        setText('other_1_val_' + productId, parseFloat(inputValueFloat('other_1', productId)).toFixed(2));
        setText('other_2_val_' + productId, parseFloat(inputValueFloat('other_2', productId)).toFixed(2));
        setText('other_total_' + productId, fmtCents(calc.other.otherTotal));
        setText('final_other_' + productId, fmtCents(calc.other.otherTotal));
    }

    if (calc.finalValues) {
        const qty = parseInt(inputValueRaw('qty', productId), 10) || 1;

        // Нижняя детальная таблица «Final values» — значения ЗА ЕДИНИЦУ
        // (qty умножается в grand-итогах и при сохранении).
        setText('subtotal_' + productId, fmtCents(calc.finalValues.subtotal));
        setText('trade_discount_val_' + productId, fmtCents(calc.finalValues.tradeDiscountAmount));
        setText('total_with_discount_' + productId, fmtCents(calc.finalValues.totalWithDiscount));
        setText('price_' + productId, fmtCents(calc.finalValues.totalWithDiscount));

        // Верхняя сводная таблица товара — СТРОЧНЫЕ итоги (× qty), согласованы:
        // Total (до скидки) = Discount + Total with discount.
        setText('total_' + productId, fmtCents(calc.finalValues.subtotal * qty));
        setText('discount_' + productId, fmtCents(calc.finalValues.tradeDiscountAmount * qty));
        setText('row_total_with_discount_' + productId, fmtCents(calc.finalValues.totalAmount));
    }

    updateGrandTotals();
}

export function calculateAllProducts() {
    document.querySelectorAll('.adm_req_brand_item_info').forEach(el => {
        calculateProduct(el.dataset.productId);
    });
    updateGrandTotals();
}

export function updateGrandTotals() {
    // textById возвращает уже EUR-decimal (UI-значения в текстовых ячейках).
    let grandSubtotal = 0;
    let grandDiscount = 0;
    let grandTotal = 0;

    document.querySelectorAll('.adm_req_brand_item_info').forEach(el => {
        const productId = el.dataset.productId;
        const qty = parseInt(inputValueRaw('qty', productId), 10) || 1;

        const subtotal = textById('subtotal_' + productId);
        const discount = textById('trade_discount_val_' + productId);
        const total = textById('total_with_discount_' + productId);

        grandSubtotal += subtotal * qty;
        grandDiscount += discount * qty;
        grandTotal += total * qty;
    });

    const fmt = (v) => parseFloat(v || 0).toFixed(2);
    setText('grand_subtotal', fmt(grandSubtotal));
    setText('grand_discount', fmt(grandDiscount));
    setText('grand_total', fmt(grandTotal));
}

/**
 * Собирает payload для save/approve. Money-поля → integer cents.
 * Перед сбором вызывает пересчёт, чтобы final_price_per_unit (cents) был актуален.
 */
export function collectProductData(productId) {
    calculateProduct(productId);

    // Текстовые ячейки UI хранят EUR-decimal; на send переводим в cents.
    const totalWithDiscountCents = eurToCents(textById('total_with_discount_' + productId));
    const tradeDiscountAmountCents = eurToCents(textById('trade_discount_val_' + productId));
    const invoicePricePerUnitCents = eurToCents(textById('final_invoice_' + productId));
    const completePerUnitCents = eurToCents(textById('final_complete_' + productId));
    const otherPerUnitCents = eurToCents(textById('final_other_' + productId));

    return {
        product_id: productId,
        product_qty: inputValueRaw('qty', productId),

        // H.2.c: money-поля payload → integer cents.
        final_price_per_unit: totalWithDiscountCents,
        trade_discount_amount: tradeDiscountAmountCents,
        invoice_price_per_unit: invoicePricePerUnitCents,
        complete_per_unit: completePerUnitCents,
        other_per_unit: otherPerUnitCents,

        // Параметры для сохранения в БД.
        short_description: textareaValue('comment', productId),
        width: inputValueRaw('width', productId),
        height: inputValueRaw('height', productId),
        depth: inputValueRaw('depth', productId),
        rrp: eurToCents(inputValueRaw('rrp', productId)),
        discount_rrp: inputValueRaw('discount_rrp', productId),
        discount_extra: inputValueRaw('discount_extra', productId),
        dealer_tax: inputValueRaw('dealer_tax', productId),
        invoice_other_discount: eurToCents(inputValueRaw('invoice_other_discount', productId)),

        // Логистика — money cents.
        logistics_customs: inputValueRaw('logistics_customs', productId),
        logistics_packaging: inputValueRaw('logistics_packaging', productId),
        logistics_certificate: inputValueRaw('logistics_certificate', productId),
        logistics_other: inputValueRaw('logistics_other', productId),
        volume_for_order: inputValueRaw('volume_for_order', productId),
        pickup: eurToCents(inputValueRaw('pickup', productId)),
        transport_tarif: eurToCents(inputValueRaw('transport_tarif', productId)),
        insurance: inputValueRaw('insurance', productId),
        tariff: inputValueRaw('tariff', productId),

        // Complete
        complete_tax: inputValueRaw('complete_tax', productId),
        complete_other: eurToCents(inputValueRaw('complete_other', productId)),
        complete_other_2: eurToCents(inputValueRaw('complete_other_2', productId)),

        // Other
        other_1: eurToCents(inputValueRaw('other_1', productId)),
        other_2: eurToCents(inputValueRaw('other_2', productId)),
        other_3: eurToCents(inputValueRaw('other_3', productId)),

        // Дизайнер (percent — int 0..100, не money).
        designer_extra_charge: inputValueRaw('designer_extra_charge', productId),
        designer_discount: inputValueRaw('designer_discount', productId),
        comission: inputValueRaw('comission', productId),

        // Trade discount — percent (int 0..100), не money.
        trade_discount: inputValueRaw('trade_discount', productId),

        // Комментарии (только внутренние — clients-комментарий теперь идёт через чат)
        manager_internal_notes: textareaValue('comments_calculation', productId),
    };
}
